import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import { ConditionalWrapper } from '@packages/shared/src/ui/ConditionalWrapper/ConditionalWrapper';
import { cn } from '@packages/shared/src/utils/cn/cn';
import type { Alignment, Distance } from '../../../interfaces';
import { ComponentTypes } from '../../../interfaces';
import { useFilterTimeControl } from '../../hooks/useFilterTimeControl';
import { registerComponents } from '../ComponentRecognizer/componentRegistry';
import { CmsGroupItem } from './CmsGroupItem';
import type { CmsGroupProps } from './types';

const mapVerticalAlignment = (type: Alignment) => {
  if (type === 'top') {
    return 'flex-start';
  }
  if (type === 'bottom') {
    return 'flex-end';
  }
  return type;
};

const BypassWrapper = (byPassLabel?: string) =>
  // eslint-disable-next-line func-names
  function (ch: React.ReactNode) {
    return <BypassBlock buttonText={byPassLabel ?? ''}>{ch}</BypassBlock>;
  };

const DistanceWrapper = (distanceType?: Distance) =>
  // eslint-disable-next-line func-names
  function (ch: React.ReactNode) {
    return <div className={`${distanceType === 'variation-1' ? 'px-3 lg:px-0' : ''}`}>{ch}</div>;
  };

export const CmsGroup = ({ data, className }: CmsGroupProps) => {
  const {
    field_cells_per_row: cellsPerRowDesktop = data.paragraph ? data.paragraph.length : 0,
    field_cells_per_row_mobile: cellsPerRowMobile = Math.floor(cellsPerRowDesktop / 2) || 1,
    field_col_gap: colGap = '0',
    field_row_gap: rowGap = '0',
    field_margin: margin = '0',
    field_padding: padding = '0',
    field_background_color: backgroundColor = 'transparent',
    field_alignment: horizontalAlignment = 'center',
    field_vertical_alignment: verticalAlignment = 'flex-start',
    field_custom_id: dynamicYieldId,
    field_distance: distanceType,
    field_skip_link_label: byPassLabel,
    behavior_paragraph_borderRadius: borderRadius,
    id,
  } = data;
  const filterTimeControl = useFilterTimeControl();
  const contentElements = data.paragraph?.filter((item) => filterTimeControl(item));

  if (!contentElements.length || contentElements.length === 0) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  return (
    <ConditionalWrapper condition={Boolean(byPassLabel)} wrapper={BypassWrapper(byPassLabel)}>
      <ConditionalWrapper
        condition={distanceType === 'fullwidth' || distanceType === 'variation-1'}
        wrapper={DistanceWrapper(distanceType)}
      >
        <div
          key={`group-${id}`}
          className={cn(
            className,
            'flex flex-wrap',
            'cms-group',
            horizontalAlignment === 'center' && 'text-center',
            horizontalAlignment === 'left' && 'text-left',
            horizontalAlignment === 'right' && 'text-right',
          )}
          style={{
            background: backgroundColor,
            borderRadius,
            margin,
            padding,
            justifyContent: horizontalAlignment,
            alignItems: mapVerticalAlignment(verticalAlignment),
          }}
          data-dy={dynamicYieldId}
        >
          {contentElements.map(
            (item, index) =>
              item && (
                <CmsGroupItem
                  key={item.id}
                  item={item}
                  rowGap={rowGap}
                  index={index}
                  colGap={colGap}
                  cellsPerRowDesktop={cellsPerRowDesktop}
                  cellsPerRowMobile={cellsPerRowMobile}
                />
              ),
          )}
        </div>
      </ConditionalWrapper>
    </ConditionalWrapper>
  );
};

// Needed to run this component in Storybook and Jest tests. Register the component in a global componentRegistry object
registerComponents({
  [ComponentTypes.Group]: CmsGroup,
  [ComponentTypes.GroupLocalizable]: CmsGroup,
  [ComponentTypes.GroupApp]: CmsGroup,
});
